import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { getUnscoredLeadCount, gradeMissingLeads, patchClientConnections } from '@/data/client';
import { apiErrMsg } from './util';
import type { ClientFormProps } from './types';
import { CONNECTION_TABS, connectionsFromClient, pickConnections } from './data';
import { useDraft } from './useDraft';
import { SaveFooter } from './SaveFooter';

const KEYS = CONNECTION_TABS.leads;

/* Where leads come from. The BigQuery table is the only field here because the
   other source — direct Wufoo/Formspree capture — is its own section with its own
   save, rendered below this on the same tab. The sync status sits next to the
   field it describes, so a wrong table id and its consequence are one glance. */
export function LeadsForm({ client, onSaved, onDirtyChange }: ClientFormProps) {
  const f = useDraft({
    formId: 'leads',
    initial: connectionsFromClient(client),
    keys: KEYS,
    persist: (d) => patchClientConnections(client.orgId, pickConnections(d, KEYS)),
    onSaved, onDirtyChange,
  });
  const { draft: conn, set: setC } = f;

  return (
    <div className="admin-form">
      <div className="admin-form__body">
        <section className="admin-sect">
          <h4 className="admin-sect__title">BigQuery lead table</h4>
          <div className="admin-prof">
            <Input
              label="BigQuery table"
              hint="Table name only — the project and dataset are fixed. Letters, digits and underscores, no spaces (e.g. Ace_Midwest_Moving)."
              placeholder="table name"
              value={conn.bqTable}
              onChange={(e) => setC('bqTable', e.target.value)}
            />
            {client.leadsSyncError ? (
              <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0, color: 'var(--red-600)' }}>
                <Icon name="alert-circle" size={12} /> Last sync failed{client.leadsSyncErrorAt ? ` (${client.leadsSyncErrorAt.slice(0, 16).replace('T', ' ')} UTC)` : ''}: {client.leadsSyncError}
              </p>
            ) : client.leadsSyncedAt ? (
              <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                Last synced {client.leadsSyncedAt.slice(0, 16).replace('T', ' ')} UTC · {client.leadCount.toLocaleString()} lead{client.leadCount === 1 ? '' : 's'} stored.
              </p>
            ) : client.bqTable ? (
              <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>Not synced yet — the hourly sync will pick it up, or use “Sync all now” on the clients list.</p>
            ) : null}
          </div>
        </section>
        <UnscoredLeads />
      </div>
      <SaveFooter dirty={f.dirty} saving={f.saving} err={f.err} onSave={f.save} label="Save lead table" sticky={false} />
    </div>
  );
}

/* Imported and webhook-captured leads that never got an AI score — a grader
   outage, or an import with grading switched off. Scores them in small
   batches with the same grader a fresh lead gets. BigQuery-fed leads are
   not offered: their score comes from the source table on every sync. */
function UnscoredLeads() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'unscored'], queryFn: getUnscoredLeadCount });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const count = data?.count ?? 0;
  if (!count && !msg) return null;
  const run = async () => {
    setBusy(true); setMsg('');
    try {
      let total = 0;
      for (let round = 0; round < 60; round++) {
        const r = await gradeMissingLeads();
        total += r.done;
        setMsg(`Scored ${total}, ${r.remaining} to go…`);
        if (r.skipped) { setMsg(`Scored ${total}. ${r.skipped}`); break; }
        if (r.remaining === 0 || r.done === 0) { setMsg(`Scored ${total} lead${total === 1 ? '' : 's'}.`); break; }
      }
    } catch (e) { setMsg(apiErrMsg(e, 'Scoring failed.')); }
    finally { setBusy(false); qc.invalidateQueries({ queryKey: ['admin', 'unscored'] }); qc.invalidateQueries({ queryKey: ['leads'] }); }
  };
  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">AI scoring</h4>
      <div className="admin-prof">
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="outline" size="sm" disabled={busy || !count} onClick={run}>{busy ? 'Scoring…' : `Score ${count} unscored lead${count === 1 ? '' : 's'}`}</Button>
          {msg && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{msg}</span>}
        </div>
        <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>Imported and webhook leads with a message but no AI summary or score. Runs the same grader a new lead gets, a few at a time.</p>
      </div>
    </section>
  );
}
